'use client';

import { Button } from '@ahafood/ui/components/button';
import { Calendar } from '@ahafood/ui/components/calendar';
import { Checkbox } from '@ahafood/ui/components/checkbox';
import { Image } from '@ahafood/ui/components/image';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@ahafood/ui/components/popover';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { format, addDays, startOfDay } from 'date-fns';
import { vi } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { trackEvent } from '@/lib/event-tracking';
import {
  WEEKDAYS,
  type WeekDay,
  useAIPlannerStore,
  DATE_RANGE_OPTIONS,
} from '@/store/ai-planner';

export function StepDeliveryDays() {
  const currentStep = useAIPlannerStore.use.currentStep();
  const preferences = useAIPlannerStore.use.preferences();
  const setPreferences = useAIPlannerStore.use.setPreferences();
  const nextStep = useAIPlannerStore.use.nextStep();

  const [selectedRange, setSelectedRange] = useState<number | null>(null);

  const selectedDays = useMemo(() => {
    return preferences.deliveryDays ?? [];
  }, [preferences.deliveryDays]);

  const allWeekdaysSelected = useMemo(() => {
    return WEEKDAYS.every((day) => selectedDays.includes(day.value));
  }, [selectedDays]);

  const today = useMemo(() => startOfDay(new Date()), []);

  const startDate = useMemo(() => {
    return preferences.startDate ? new Date(preferences.startDate) : today;
  }, [preferences.startDate, today]);

  const endDate = useMemo(() => {
    return preferences.endDate ? new Date(preferences.endDate) : null;
  }, [preferences.endDate]);

  const handleDayToggle = useCallback(
    (day: WeekDay) => {
      const newDays = selectedDays.includes(day)
        ? selectedDays.filter((d) => d !== day)
        : [...selectedDays, day];
      setPreferences({ deliveryDays: newDays });
    },
    [selectedDays, setPreferences]
  );

  const handleAllWeekdaysToggle = useCallback(() => {
    if (allWeekdaysSelected) {
      setPreferences({ deliveryDays: [] });
    } else {
      setPreferences({ deliveryDays: WEEKDAYS.map((d) => d.value) });
    }
  }, [allWeekdaysSelected, setPreferences]);

  const handleQuickSelect = useCallback(
    (weeks: number, days: number) => {
      setSelectedRange(weeks);
      const start = startDate;
      const end = addDays(start, days - 1);
      setPreferences({
        endDate: end.toISOString(),
      });
    },
    [startDate, setPreferences]
  );

  const handleStartDateChange = useCallback(
    (date: Date | undefined) => {
      if (!date) return;
      setSelectedRange(null);
      // Auto-calculate end date if duration is set
      if (endDate && preferences.startDate) {
        const currentDuration = Math.ceil(
          (new Date(preferences.endDate!).getTime() -
            new Date(preferences.startDate).getTime()) /
            (1000 * 60 * 60 * 24)
        );
        setPreferences({
          startDate: date.toISOString(),
          endDate: addDays(date, currentDuration).toISOString(),
        });
      } else {
        setPreferences({ startDate: date.toISOString() });
      }
    },
    [endDate, preferences, setPreferences]
  );

  const handleEndDateChange = useCallback(
    (date: Date | undefined) => {
      if (!date) return;
      setSelectedRange(null);
      setPreferences({ endDate: date.toISOString() });
    },
    [setPreferences]
  );

  const canProceed =
    selectedDays.length > 0 && preferences.startDate && preferences.endDate;

  const formatDate = (date: Date) => {
    return format(date, 'dd/MM/yyyy', { locale: vi });
  };

  // Auto-set start date to today if not set
  useEffect(() => {
    if (!preferences.startDate) {
      setPreferences({ startDate: today.toISOString() });
    }
  }, [preferences.startDate, setPreferences, today]);

  return (
    <div className="">
      <div className="absolute -top-40 inset-x-0 w-full h-auto flex items-start justify-center py-4 aspect-square">
        <Image
          src={`${assetPrefix}/static/icons/ai-planner-page-2-bg.svg`}
          alt="planner delivery days background"
          fill
          className="object-contain"
          priority
        />
      </div>
      <ScrollArea className="flex-1 w-full max-h-[calc(100dvh-123px-88px-24px)] overflow-auto">
        <div className="flex flex-col gap-0 font-bold text-2xl mb-6 mt-4">
          <span className="text-blue-40">Bạn muốn bữa trưa được</span>
          <span className="text-white bg-primary-40 rounded-full px-3 w-fit">
            giao đến ngày nào?
          </span>
        </div>
        {/* Weekday Selection */}
        <div className="mb-4 w-full items-center flex flex-col gap-2">
          <p className="text-white text-sm font-semibold flex">
            Chọn ngày giao trong tuần{' '}
            <p className="text-red-50 mb-4 ml-3 text-xl">*</p>
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            {WEEKDAYS.map((day) => (
              <button
                key={day.value}
                type="button"
                onClick={() => handleDayToggle(day.value)}
                className={cn(
                  'p-4 rounded-full text-xs font-semibold transition-all size-12 flex items-center justify-center text-white whitespace-nowrap',
                  'border border-white/20',
                  selectedDays.includes(day.value)
                    ? 'ai-planner-button-active'
                    : 'ai-planner-button'
                )}
              >
                {day.label}
              </button>
            ))}
          </div>
          {/* All Weekdays Checkbox */}
          <div className="flex items-center gap-3 py-2">
            <Checkbox
              id="all-weekdays"
              checked={allWeekdaysSelected}
              onCheckedChange={handleAllWeekdaysToggle}
              className="border-white/30 data-[state=checked]:bg-[#ff8200] data-[state=checked]:border-[#ff8200]"
            />
            <label
              htmlFor="all-weekdays"
              className="text-white text-sm cursor-pointer"
            >
              Cả tuần làm việc
            </label>
          </div>
        </div>

        {/* Start Date */}
        <div className="mb-4">
          <p className="text-white text-sm font-semibold flex">
            Thời gian bắt đầu kế hoạch{' '}
            <p className="text-red-50 mb-4 ml-3 text-xl">*</p>
          </p>
          <Popover modal={false}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-between text-left text-base font-normal bg-white/5 ai-planner-input rounded-xl px-4 py-3 text-white hover:bg-white/10 hover:text-white h-auto border-none"
              >
                <span>
                  {preferences.startDate
                    ? `${formatDate(new Date(preferences.startDate))}`
                    : 'Chọn ngày bắt đầu'}
                </span>
                <CalendarIcon className="size-5 text-white/40" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-0 border-0 shadow-2xl"
              align="start"
              style={{
                zIndex: 99999,
                background:
                  'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.18)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
              }}
            >
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={handleStartDateChange}
                disabled={(date) => date < today}
                defaultMonth={startDate}
                locale={vi}
                className="rounded-[20px] p-3 text-white [&_.rdp-day_button]:text-white [&_.rdp-day_button:hover]:bg-white/20 [&_.rdp-day_selected]:bg-primary [&_.rdp-caption]:text-white [&_.rdp-nav_button]:text-white/60 [&_.rdp-nav_button:hover]:bg-white/20 [&_.rdp-weekday]:text-white/60"
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* End Date */}
        <div className="mb-4">
          <p className="text-white text-sm font-semibold flex">
            Thời gian kết thúc kế hoạch{' '}
            <p className="text-red-50 mb-4 ml-3 text-xl">*</p>
          </p>
          <Popover modal={false}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                disabled={!preferences.startDate}
                className={cn(
                  'w-full justify-between text-left text-base font-normal bg-white/5 ai-planner-input rounded-xl px-4 py-3 text-white hover:bg-white/10 hover:text-white h-auto border-none'
                )}
              >
                <span className={!endDate ? 'text-white/40' : ''}>
                  {endDate ? formatDate(endDate) : 'Điền ngày kết thúc'}
                </span>
                <CalendarIcon className="size-5 text-white/40" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-0 border-0 shadow-2xl"
              align="start"
              style={{
                zIndex: 99999,
                background:
                  'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.18)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
              }}
            >
              <Calendar
                mode="single"
                selected={endDate ?? undefined}
                onSelect={handleEndDateChange}
                disabled={(date) => date < startDate}
                defaultMonth={endDate ?? startDate}
                locale={vi}
                className="ai-planner-input rounded-[20px] p-3 text-white [&_.rdp-day_button]:text-white [&_.rdp-day_button:hover]:bg-white/20 [&_.rdp-day_selected]:bg-primary [&_.rdp-caption]:text-white [&_.rdp-nav_button]:text-white/60 [&_.rdp-nav_button:hover]:bg-white/20 [&_.rdp-weekday]:text-white/60"
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Quick Select */}
        <div className="flex gap-2 mb-4">
          {DATE_RANGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleQuickSelect(option.value, option.days)}
              className={cn(
                'flex-1 px-3 py-2 rounded-full text-sm font-medium transition-all text-white',
                selectedRange === option.value
                  ? 'ai-planner-button-active'
                  : 'ai-planner-button'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Info Text */}
        <p className="text-white/40 text-xs mb-6">
          1 tuần tính từ ngày bắt đầu đến hết thứ 6 cùng tuần (chỉ tính các ngày
          từ thứ 2 đến thứ 6, không tính cuối tuần)
        </p>

        {/* Spacer for bottom button */}
        <div className="h-24" />

        {/* CTA Button */}
      </ScrollArea>
      <div className="fixed bottom-0 inset-x-0 pb-4 pt-2 px-4">
        <div className=" glass-bubble rounded-full flex justify-center px-4 py-2 h-16">
          <Button
            onClick={() => {
              trackEvent('meal_plan', {
                event_name: 'click_button',
                button_name: 'meal_plan_step',
                button_location: currentStep,
              });
              nextStep();
            }}
            disabled={!canProceed}
            className={cn(
              'size-full rounded-full bg-[#0089FF] text-lg font-semibold'
            )}
          >
            <Image
              width={22}
              height={22}
              className="mr-2 object-contain"
              src={`${assetPrefix}/static/icons/white-ai.svg`}
              alt={''}
            />{' '}
            Xem trước thực đơn với AI
          </Button>
        </div>
      </div>
    </div>
  );
}
